import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import {
  Typography,
  TextField,
  Button,
  Modal,
  InputBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Icon } from '@iconify/react';
import axiosInstance from 'src/api/axiosInstance';
import { useSnackbar } from 'src/components/snackbar';
import { useRouter } from 'src/routes/hooks';
import { getCurrentProject, getCurrentServer, getMandEndPoint } from 'src/helper/server-address';
import { paths } from 'src/routes/paths';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Logo from 'src/components/logo';
import { decryptData, encryptData } from '../../utils/Crypto';

export default function EmployeeFormLayout({ children }) {
  const [searchParams] = useSearchParams();
  const rawEmailParam = searchParams.get('email');

  // Decrypt email parameter upfront before assigning or using in component
  const prefilledEmail = React.useMemo(() => {
    if (!rawEmailParam) return '';
    try {
      const decoded = decodeURIComponent(rawEmailParam);
      const decrypted = decryptData(rawEmailParam) || decryptData(decoded) || decryptData(decoded.replace(/ /g, '+'));
      if (decrypted && decrypted.includes('@')) return decrypted;
      if (decoded && decoded.includes('@')) return decoded;
      if (rawEmailParam.includes('@')) return rawEmailParam;
    } catch (e) {
      // ignore
    }
    return rawEmailParam;
  }, [rawEmailParam]);

  const [email, setEmail] = useState(() => prefilledEmail);
  const [otpArray, setOtpArray] = useState(Array(6).fill(''));
  const inputRefs = useRef([]);
  const [showOtpField, setShowOtpField] = useState(false);
  const [modalOpen, setModalOpen] = useState(true);
  const [otpError, setOtpError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [blockEndTime, setBlockEndTime] = useState(null);
  const [sendDisabled, setSendDisabled] = useState(false);
  const [isPermanentBlock, setIsPermanentBlock] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const [isLinkExpired, setIsLinkExpired] = useState(false);
  const [isAlreadySubmitted, setIsAlreadySubmitted] = useState(false);
  const expParam = searchParams.get('exp');


  useEffect(() => {
    let isExpired = false;
    if (expParam) {
      try {
        const decryptedExp = decryptData(expParam);
        
        if (decryptedExp && !Number.isNaN(Number(decryptedExp))) {
          const expiryTime = Number(decryptedExp);
          if (Date.now() > expiryTime) {
            setIsLinkExpired(true);
            setModalOpen(true);
            isExpired = true;
          }
        }
      } catch (e) {
        // ignore
      }
    }

    if (!isExpired) {
      if (prefilledEmail) {
        setEmail(prefilledEmail);
        
        const normalizedEmail = prefilledEmail ? prefilledEmail.toLowerCase().trim() : '';
        if (normalizedEmail && localStorage.getItem(`emp_submitted_${normalizedEmail}`) === 'true') {
          setIsAlreadySubmitted(true);
        }

        setModalOpen(true);
      } else {
        setModalOpen(false);
      }
    }
  }, [prefilledEmail, expParam]);

  // Check block status from backend
  useEffect(() => {
     if (!email || isLinkExpired || isAlreadySubmitted) return;;

    const checkBlockStatus = async () => {
      try {
        const res = await axiosInstance.get(`/rest/employee/otp/check-block-status`, {
          params: { email },
        });

        const { blocked, secondsRemaining, permanentBlock } = res.data;

        if (permanentBlock) {
          setIsPermanentBlock(true);
          setSendDisabled(true);
          setOtpError('You are permanently blocked. Contact administrator.');
          setBlockEndTime(null);
        } else if (blocked && secondsRemaining > 0) {
          setIsPermanentBlock(false);
          setSendDisabled(true);
          setBlockEndTime(Date.now() + secondsRemaining * 1000);
          setOtpError('You are temporarily blocked. Please wait.');
        } else {
          setIsPermanentBlock(false);
          setSendDisabled(false);
          setOtpError('');
          setBlockEndTime(null);
        }
      } catch (err) {
        console.error('Failed to check block status:', err);
      }
    };

    checkBlockStatus();
  }, [email, isAlreadySubmitted, isLinkExpired]);

  // Countdown effect using absolute time
  useEffect(() => {
    if (!blockEndTime) return undefined;

    const updateRemaining = () => {
      const diff = Math.max(0, Math.floor((blockEndTime - Date.now()) / 1000));
      setRemainingTime(diff);

      if (diff === 0) {
        clearInterval(timerRef.current);
        setBlockEndTime(null);
        setOtpError('');
        setSendDisabled(false);
        setIsPermanentBlock(false);
      }
    };

    updateRemaining();
    timerRef.current = setInterval(updateRemaining, 1000);

    return () => clearInterval(timerRef.current);
  }, [blockEndTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`;
  };

  const handleSendOtp = async (emailToSend = email) => {
    setOtpError('');
    setIsLoading(true);
    if (isPermanentBlock) {
      setIsLoading(false);
      return;
    }

    try {
      await axiosInstance.post(`/rest/candidate/otp/send`, { email: emailToSend });
      setShowOtpField(true);
      setOtpArray(Array(6).fill(''));
      enqueueSnackbar('OTP sent successfully!', { variant: 'success' });
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        setOtpError('No employee found with this email address.');
      } else if (status === 429) {
        const backendMessage = err.response?.data?.error || '';
        const waitSeconds = parseInt(err.response.data.secondsToWait || '60', 10);
        setBlockEndTime(Date.now() + waitSeconds * 1000);
        setSendDisabled(true);

        if (backendMessage.toLowerCase().includes('temporarily blocked')) {
          setOtpError('You have been temporarily blocked due to multiple requests.');
        } else if (backendMessage.toLowerCase().includes('must wait')) {
          setOtpError('Please wait a moment before requesting a new OTP.');
        } else {
          setOtpError('Too many attempts detected. Please try again later.');
        }
      } else if (status === 403) {
        setOtpError(
          'Your account has been permanently blocked. Please contact the administrator for assistance.'
        );
        setIsPermanentBlock(true);
        setSendDisabled(true);
      } else {
        enqueueSnackbar('Unable to send OTP at the moment. Please try again later.', {
          variant: 'error',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const fullOtp = otpArray.join('');

    if (fullOtp.length !== 6) {
      setOtpError('Please enter complete OTP');
      return;
    }
    setIsLoading(true);

    try {
      const encryptedEmail = encryptData(email);
      const encryptedOtp = encryptData(fullOtp);

      // Step 1: Verify OTP
      const res = await axiosInstance.post('/rest/candidate/otp/verify', {
        encryptedEmail,
        encryptedOtp,
      });

      if (!res.data) {
        enqueueSnackbar('Invalid OTP', { variant: 'error' });
        return;
      }

      // OTP verified, reset input
      setOtpArray(Array(6).fill(''));
      enqueueSnackbar('Email and OTP Verified Successfully!', { variant: 'success' });

      // Step 2: Fetch candidate by email
      let candidateId;
      try {
        const candidateRes = await axiosInstance.get(
          `/rest/candidate/otp/get-candidate-by-email`,
          {
            params: { email: encryptedEmail },
            withCredentials: false,
          }
        );
        candidateId = candidateRes.data?.candidateId;
        if (!candidateId) {
          // No candidate found → new candidate flow
          setModalOpen(false);
          setIsVerified(true);
          return;
        }
      } catch (err) {
        console.error('Error fetching candidate by email:', err);
        enqueueSnackbar('Failed to fetch candidate details', { variant: 'error' });
        return;
      }

      // Step 3: Check if employee exists for this candidate
      try {
        const empRes = await axiosInstance.get(`/rest/candidate/external/by-candidate-id/${candidateId}`);

        if (empRes.data?.exists && empRes.data?.employeeId) {
          // Employee exists → navigate to edit form
          navigate(paths.employeeEditForm(empRes.data.employeeId));
        } else {
          // Employee does not exist → navigate to new employee form
          navigate(`/employeeForm/candidate/${candidateId}`);
        }

        setModalOpen(false);
        setIsVerified(true);
      } catch (empErr) {
        console.error('Error checking employee existence:', empErr);
        enqueueSnackbar('Failed to check employee mapping.', { variant: 'error' });
      }
    } catch (err) {
      console.error('OTP verification failed:', err);
      enqueueSnackbar('OTP verification failed', { variant: 'error' });
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otpArray];
    newOtp[index] = value;
    setOtpArray(newOtp);
    setOtpError('');
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otpArray[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim();
    const digits = pasted.split('').filter((ch) => /\d/.test(ch));
    if (digits.length === otpArray.length) {
      setOtpArray(digits);
      setOtpError('');
    }
  };

  const handleResendOtp = () => {
    setOtpArray(Array(6).fill(''));
    setOtpError('');
    handleSendOtp();
  };

  const getButtonLabel = () => {
    if (isLoading) return 'Sending...';
    if (isPermanentBlock) return 'Permanently Blocked';
    if (remainingTime > 0) return `Please Wait ${formatTime(remainingTime)}`;
    return 'Send Code';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo */}
      <Box>
        <Logo sx={{ width: 160, height: 'auto', m: '20px 0 10px 25px' }} />

        {/* Header */}
        <Box
          sx={{
            bgcolor: '#f5f5f5',
            p: 2,
            borderRadius: 2,
            maxWidth: '1100px',
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <Icon icon="mdi:account-box-outline" width={35} height={35} color="black" />
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Employee Onboarding Form
          </Typography>
        </Box>

        {/* Modal */}
        {email && (
          <Modal open={modalOpen}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '90%', sm: 460 },
                bgcolor: '#fff',
                borderRadius: 2,
                p: 3,
                boxShadow: 3,
                overflow: 'hidden',
              }}
            >
              {/* Header Section */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    borderRadius: '50%',
                    bgcolor: '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  <Icon icon="mdi:email-lock" width={30} height={30} />
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  {showOtpField ? 'Account Verification' : 'OTP Verification'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {showOtpField
                    ? 'Enter the code below to verify your email'
                    : 'We will send the code to your email'}
                </Typography>
              </Box>

              {/* Email Display */}
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Email Address
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon icon="mdi:email" width={20} height={20} color="#555" />
                  <Typography variant="body2" fontWeight={500}>
                    {email}
                  </Typography>
                </Box>
              </Box>

              {/* OTP / Send OTP */}
              {!showOtpField ? (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => handleSendOtp()}
                    disabled={sendDisabled || remainingTime > 0 || isPermanentBlock || isLoading}
                    sx={{ textTransform: 'none', py: 1.5, borderRadius: 1 }}
                  >
                    {getButtonLabel()}
                  </Button>

                  {otpError && (
                    <Box
                      sx={{
                        backgroundColor:
                          otpError.toLowerCase().includes('Please wait') ||
                            otpError.toLowerCase().includes('temporarily blocked')
                            ? '#ebb11dff' // soft yellow (temporary block)
                            : '#eea0a2ff', // soft red (permanent or other errors)
                        color: 'black',
                        borderRadius: 1,
                        p: 1,
                        mt: 1,
                        textAlign: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        border: '1px solid',
                        borderColor:
                          otpError.toLowerCase().includes('wait') ||
                            otpError.toLowerCase().includes('temporarily')
                            ? '#ffcc80' // light yellow border
                            : '#ef9a9a', // light red border
                      }}
                    >
                      {otpError}
                    </Box>
                  )}
                </>
              ) : (
                <>
                  {/* OTP Input */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 2 }}>
                    {otpArray.map((digit, index) => (
                      <InputBase
                        key={index}
                        inputRef={(el) => {
                          if (el) inputRefs.current[index] = el;
                        }}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        inputProps={{
                          maxLength: 1,
                          style: {
                            width: '100%',
                            height: '50px',
                            fontSize: '1.2rem',
                            textAlign: 'center',
                            border: '1px solid #ccc',
                            borderRadius: 8,
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                        sx={{ flex: 1 }}
                      />
                    ))}
                  </Box>

                  {otpError && (
                    <Box
                      sx={{
                        backgroundColor:
                          otpError.toLowerCase().includes('Please wait') ||
                            otpError.toLowerCase().includes('temporarily blocked')
                            ? '#ebb11dff' // soft yellow (temporary block)
                            : '#eea0a2ff', // soft red (permanent or other errors)
                        color: 'black',
                        borderRadius: 1,
                        p: 1,
                        mt: 1,
                        textAlign: 'center',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        border: '1px solid',
                        borderColor:
                          otpError.toLowerCase().includes('wait') ||
                            otpError.toLowerCase().includes('temporarily')
                            ? '#ffcc80' // light yellow border
                            : '#ef9a9a', // light red border
                      }}
                    >
                      {otpError}
                    </Box>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleVerifyOtp}
                    disabled={isLoading || otpArray.join('').length !== 6}
                    sx={{ textTransform: 'none', py: 1.5, mb: 1, mt: 1, borderRadius: 1 }}
                  >
                    {isLoading ? 'Verifying...' : 'Verify Code'}
                  </Button>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%', // takes full height of parent container
                      mt: 2,
                    }}
                  >
                    <Typography component="span" sx={{ color: '#333', mr: 0.5 }}>
                      Didn’t receive code?
                    </Typography>

                    <Typography
                      component="span"
                      sx={{
                        color: 'green',
                        fontWeight: 600,
                        cursor:
                          sendDisabled || remainingTime > 0 || isLoading
                            ? 'not-allowed'
                            : 'pointer',
                        opacity: sendDisabled || remainingTime > 0 || isLoading ? 0.6 : 1,
                        textDecoration: 'underline',
                      }}
                      onClick={
                        sendDisabled || remainingTime > 0 || isLoading ? undefined : handleResendOtp
                      }
                    >
                      {remainingTime > 0
                        ? `Resend Code (${formatTime(remainingTime)})`
                        : 'Resend Code'}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </Modal>
        )}

        <Box component="main">{children}</Box>
      </Box>
    </Box>
  );
}

EmployeeFormLayout.propTypes = {
  children: PropTypes.node,
};
